// Copyright (c) Docugami, Inc. All rights reserved.

import { createFolderId } from "components/file_management/file_helpers";
import { DgmlInstancesSingleton } from "hooks/documentDgml";
import { TokenRange } from "networking/api";
import { Page, getTokenRangesForPages } from "sdv/Page";
import { transformBoxPageToWorldSpace } from "sdv/Space";
import { createDefaultBoundsComponent } from "sdv/components/BoundsComponent";
import { createChunkRenderComponent } from "sdv/components/ChunkRenderComponent";
import { DefaultControlColorComponent } from "sdv/components/ColorComponent";
import { ControlComponent, ControlType } from "sdv/components/ControlComponent";
import { createOutflowRenderComponent } from "sdv/components/OutflowRenderComponent";
import { SimpleTableColumnHeaderComponent } from "sdv/components/SimpleTableColumnHeaderComponent";
import { createTableChunkRenderComponent } from "sdv/components/TableChunkRenderComponent";
import { TokenRangeComponent } from "sdv/components/TokenRangeComponent";
import { ComponentId, tagComponent } from "sdv/engine/Component";
import { Entity, IEntityManager } from "sdv/engine/Entity";
import { store } from "store";
import { getParentTable, getSimpleColumns, isSimpleColumnHeader, isSimpleTable } from "store/dgml/dgmlActions";
import { OutflowContainerInfo, dgmlSlice, selectAllOutflowContainers, selectChunkBySpanId } from "store/dgml/dgmlSlice";
import { Chunk, ChunkStructure, isChunkAddedChunk, isOutflowStructure, isTableStructure } from "store/dgml/dgmlTypes";
import { Box } from "store/shared_types";
import { IErrorLimiter } from "utils/ErrorLimiter";
import { TokenRangeUtils } from "utils/TokenRangeUtils";
import { IResources } from "../Resources";
import { ISystem, Queries } from "../engine/Engine";

/**
 * Responsible for creating the visible controls in the scene.  It walks the DGML DOM,
 * determines which nodes are currently visible, and creates entities with ControlComponents
 * to represent each of them.
 */
export class ControlTreeSystem implements ISystem {
    get updateEnabled() { return true; }

    private _documentId: string;
    private _dgmlVersion: string | undefined;
    private _loadedPages: Set<string> = new Set();
    private _lastPos: number = 0;

    resources!: IResources;
    entities!: IEntityManager;
    queries: Queries = {
        controls: { types: [ComponentId.Control] },
        selectedControl: { types: [ComponentId.Control, ComponentId.Selected] },
        activeRoot: { types: [ComponentId.RootControl, ComponentId.Active] },
        allRoots: { types: [ComponentId.RootControl] }
    }
    errorLimiter!: IErrorLimiter;

    constructor(documentId: string) {
        this._documentId = documentId;
    }

    update() {
        this.updateControlTree();
        this.updateRootResource();
    }

    private updateControlTree() {
        const currentPos = this.resources.get('viewPosition').value.y;
        const pixelDelta = Math.abs(currentPos - this._lastPos)
        this._lastPos = currentPos;
        if (pixelDelta > 0) {
            // Don't rebuild the tree while scrolling to improve performance
            return;
        }

        const currentDgmlVersion = this.resources.get('dgml').value?.version?.value;
        if (!this.resources.get('pagesLoaded').value || !currentDgmlVersion) {
            // Without loaded pages or DGML then there's nothing we can do
            return;
        }

        const currentPages = this.resources.get('visiblePages').value;
        const pageUpdateRequired = currentPages.some(x => !this._loadedPages.has(x.id));
        if (this._dgmlVersion === currentDgmlVersion && !pageUpdateRequired) {
            // If DGML hasn't changed and we've already loaded the visible pages then nothing to do
            return;
        }

        this._dgmlVersion = currentDgmlVersion;
        this._loadedPages.clear();
        currentPages.forEach(x => this._loadedPages.add(x.id));

        const selectedControl = this.queries.selectedControl.query!.entities[0];
        for (const entity of this.queries.controls.query!.entities) {
            if (!!entity.name && entity.name !== selectedControl?.name && !entity.hasComponent(ComponentId.AddedChunk)) {
                this.entities.destroy(entity);
            }
        }

        const visibleRanges: TokenRange[] = getTokenRangesForPages(currentPages);

        const selectedTokenRange = selectedControl?.getComponent<TokenRangeComponent>(ComponentId.TokenRange);
        if (!!selectedTokenRange) {
            const selectedRange = selectedTokenRange.tokenRange;
            const isContained = visibleRanges.find(range => TokenRangeUtils.rangeIsContained(selectedRange, range));
            if (!isContained) {
                // It's possible for a resize/selection to modify the from or toTok which may not be visible on the page.
                // Ensure that we have the associated controls for those tokens
                visibleRanges.push(
                    { fromTok: selectedRange.fromTok, toTok: selectedRange.fromTok + 1 },
                    { fromTok: selectedRange.toTok - 1, toTok: selectedRange.toTok });
            }
        }

        let activeRoot: Entity | null = this.queries.activeRoot.query!.single();
        const roots = this.createControlTree(this._documentId, this.resources.get("pages").value, visibleRanges);
        for (const root of roots) {
            if (root.name === activeRoot?.name) {
                activeRoot = root;
            }
        }

        activeRoot?.ensureComponent(tagComponent(ComponentId.Active));
    }

    private updateRootResource() {
        const id = this.queries.activeRoot.query!.entities[0]?.id ?? null;
        if (this.resources.get('rootControl').value !== id) {
            this.resources.set('rootControl', id);
        }
    }

    private createControlTree(documentId: string, pages: Page[], visibleRanges: TokenRange[]): Entity[] {
        const folderId = createFolderId(documentId);
        const state = dgmlSlice.endpoints.getDgmlNodes.select({ docsetId: folderId.clusterId, fileId: folderId.fileId })(store.getState());

        if (!state.data?.root) {
            return [];
        }

        const selectChunk = (spanId: string): Chunk => {
            const result = selectChunkBySpanId(state.data!.chunks, spanId);
            if (!result) {
                throw new Error("Could not find chunk");
            }

            return result;
        }

        const inRange = (tokenRange: TokenRange): boolean => {
            for (const range of visibleRanges) {
                if (TokenRangeUtils.rangeIsContained(tokenRange, range) || TokenRangeUtils.rangesOverlap(tokenRange, range) || TokenRangeUtils.rangeIsContained(range, tokenRange)) {
                    return true;
                }
            }

            return false;
        }

        const visibleOutflowContainerInfos = selectAllOutflowContainers(state.data.outflowContainers).filter(x => !x.isEmpty && pages.some(page => x.pageNumbers.includes(page.number)));
        const defaultRoot = this.createControlTreeForRoot(
            state.data.root,
            documentId,
            pages,
            selectChunk,
            inRange,
            visibleOutflowContainerInfos.filter(x => x.rootId === state.data!.root)
        ).ensureComponent(tagComponent(ComponentId.Default));

        const outflowRoots = visibleOutflowContainerInfos.map(x => this.createControlTreeForRoot(
            x.outflowId,
            documentId,
            pages,
            selectChunk,
            inRange,
            visibleOutflowContainerInfos.filter(y => y.rootId === x.outflowId)
        ));

        return [defaultRoot, ...outflowRoots];
    }

    private createControlTreeForRoot(
        rootId: string,
        documentId: string,
        pages: Page[],
        selectChunk: (spanId: string) => Chunk,
        inRange: (tokenRange: TokenRange) => boolean,
        outflowContainers: OutflowContainerInfo[]): Entity {
        const getChildren = (entityInfo: EntityInfo): VisitNode[] => {
            return entityInfo.control.node.children
                .map(x => selectChunk(x))
                .filter(x => x.structure?.type !== ChunkStructure.OutflowContainer && inRange(x.tokenRange))
                .map(x => ({
                    node: x,
                    parent: entityInfo
                }));
        }

        const createEntity = (visitNode: VisitNode): EntityInfo | null => {
            const id = visitNode.node.spanId;
            let control: ControlComponent = {
                typeId: ComponentId.Control,
                id: id,
                type: getControlType(documentId, visitNode.node),
                node: visitNode.node,
                parent: visitNode.parent?.entity.name ?? '',
                children: [],
                fromTok: visitNode.node.tokenRange.fromTok,
                toTok: visitNode.node.tokenRange.toTok,
                isRoot: rootId === id
            };

            const tokenRange: TokenRangeComponent = {
                typeId: ComponentId.TokenRange,
                tokenRange: { ...visitNode.node.tokenRange }
            }

            let entity = this.entities.get(id);
            if (!entity || entity.isPendingDelete) {
                entity = this.entities.create(id)
                    .addComponent({ ...DefaultControlColorComponent });
            }
            else {
                // Remove the existing control component for it to be re-added below
                entity.removeComponent(ComponentId.Control);
            }

            // Ensure default components
            entity
                .ensureComponent(tokenRange)
                .ensureComponent({ typeId: ComponentId.TokenRangeInfo })
                .ensureComponent(createDefaultBoundsComponent());

            // Update the control component for the entity - we want this update to take place in the same frame that the new
            // control entities are created so we don't force an ensureComponent(control, true) here.
            entity.addComponent(control);

            // Ensure this entity's state by control type
            // Handle adding required components by type, or destroying the component as necessary
            ensureEntityStateByType(documentId, entity, control, pages, this.entities);
            return {
                entity: entity,
                control: control
            }
        }

        const queue: VisitNode[] = [{ parent: null, node: selectChunk(rootId) }]
        let root: EntityInfo | null = null;
        while (queue.length !== 0) {
            const visit = queue.shift()!;
            let visitResult: EntityInfo | null = null;
            try {
                visitResult = createEntity(visit);
            }
            catch (error) {
                this.errorLimiter.handle(error);
                visitResult = null;
            }

            if (!visitResult) {
                continue;
            }

            if (!root) {
                root = visitResult;
            }

            visit.parent?.control.children.push(visitResult.entity.name);

            if (visitResult.control.type !== ControlType.Outflow) {
                const children = getChildren(visitResult);

                if (visitResult === root) {
                    // Add all outflow containers to the root to ensure their existence as they could exist outside the visible tree.
                    for (const outflowContainer of outflowContainers) {
                        const visitNode: VisitNode = {
                            node: selectChunk(outflowContainer.id),
                            parent: root
                        }

                        children.push(visitNode);
                    }
                }

                queue.push(...children);
            }
        }

        if (!root) {
            throw new Error("Could not create valid root entity.");
        }

        root.entity.ensureComponent(tagComponent(ComponentId.RootControl))
        return root.entity;
    }
}

interface EntityInfo {
    entity: Entity
    control: ControlComponent
}

interface VisitNode {
    node: Chunk;
    parent: EntityInfo | null;
}

export function getControlType(documentId: string, node: Chunk): ControlType {
    let controlType = ControlType.SmallChunk;

    const isStructural = DgmlInstancesSingleton.containsStructuralChunkNode(node, documentId);
    if (isStructural) {
        controlType = isTableStructure(node.structure?.type) ? tableStructureToControlType(node.structure!.type) :
            isOutflowStructure(node.structure?.type) ? ControlType.Outflow : ControlType.LargeChunk;
    }

    return controlType;
}

function tableStructureToControlType(tableStructure: ChunkStructure): ControlType {
    switch (tableStructure) {
        case ChunkStructure.Table:
            return ControlType.Table;
        case ChunkStructure.TableBody:
            return ControlType.TableBody;
        case ChunkStructure.TableRow:
            return ControlType.TableRow;
        case ChunkStructure.TableCell:
            return ControlType.TableCell;
        default:
            throw new Error("No mapping from table structure to control type.");
    }
}

function controlTypeToTableStructure(controlType: ControlType): ChunkStructure {
    switch (controlType) {
        case ControlType.Table:
            return ChunkStructure.Table;
        case ControlType.TableBody:
            return ChunkStructure.TableBody;
        case ControlType.TableRow:
            return ChunkStructure.TableRow;
        case ControlType.TableCell:
            return ChunkStructure.TableCell;
        default:
            throw new Error("No mapping from control type to table structure.");
    }
}

function ensureEntityStateByType(folderId: string, entity: Entity, control: ControlComponent, pages: Page[], entities: IEntityManager): Entity | null {
    // Add some metadata for simple tables/columns
    if (control.type === ControlType.Table) {
        const isSimple = store.dispatch(isSimpleTable(folderId, control.node.spanId));
        if (!!isSimple) {
            entity.ensureComponent(tagComponent(ComponentId.SimpleTable));
        }
    }
    else if (control.type === ControlType.TableCell) {
        const isColumnHeader = store.dispatch(isSimpleColumnHeader(folderId, control.node.spanId));
        if (isColumnHeader) {
            const parentTable = store.dispatch(getParentTable(folderId, control.node.spanId))!;
            const column = store.dispatch(getSimpleColumns(folderId, parentTable.spanId)).find(x => x.header === control.node);
            if (!!column) {
                if (!!column.header.style?.boundingBoxes?.length) {
                    const rawBoxes: Box[] = [...column.header.style.boundingBoxes, ...column.rows.flatMap(x => x.style?.boundingBoxes ?? [])];
                    const boxes: Box[] = rawBoxes.flatMap(b => {
                        const page = pages.find(x => x.pageIndex + 1 === b.page);
                        return transformBoxPageToWorldSpace(page!, b);
                    });

                    entity.ensureComponent<SimpleTableColumnHeaderComponent>({
                        typeId: ComponentId.SimpleTableColumnHeader,
                        boxes: boxes
                    });
                }
            }
        }
    }

    // Add basic render components
    switch (control.type) {
        case ControlType.SmallChunk:
            entity.ensureComponent(createChunkRenderComponent());
            break;
        case ControlType.LargeChunk:
            entity.ensureComponent(createChunkRenderComponent());
            break;
        case ControlType.Table:
            entity.ensureComponent(createTableChunkRenderComponent(controlTypeToTableStructure(control.type)));
            entity.ensureComponent(tagComponent(ComponentId.Table));
            break;
        case ControlType.TableCell:
            entity.ensureComponent(createTableChunkRenderComponent(controlTypeToTableStructure(control.type)));
            entity.ensureComponent(tagComponent(ComponentId.TableCell));
            break;
        case ControlType.TableBody:
            entity.ensureComponent(createTableChunkRenderComponent(controlTypeToTableStructure(control.type)));
            break;
        case ControlType.TableRow:
            entity.ensureComponent(createTableChunkRenderComponent(controlTypeToTableStructure(control.type)));
            entity.ensureComponent(tagComponent(ComponentId.TableRow));
            break;
        case ControlType.Outflow:
            entity.ensureComponent(createOutflowRenderComponent());
            entity.ensureComponent(tagComponent(ComponentId.Outflow));
            break;
    }

    const isHyperline = !!control.node.structure && control.node.structure.type !== ChunkStructure.ListItemMarker;
    if (isHyperline) {
        entity.ensureComponent(tagComponent(ComponentId.Hyperline));
    }

    const isAddedChunk = isChunkAddedChunk(control.node);
    if (isAddedChunk) {
        entity.ensureComponent(tagComponent(ComponentId.AddedChunk));
    }

    return entity;
}
